import { AlertCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { IncidentPanel } from "@/modules/dashboard/payment-incidents/components/IncidentPanel";
import { IncidentsSummaryStrip } from "@/modules/dashboard/payment-incidents/components/IncidentsSummaryStrip";
import { usePaymentIncidentsSummary } from "@/modules/dashboard/payment-incidents/hooks/usePaymentIncidentsSummary";
import { canOpenIncidentOn } from "@/modules/dashboard/payment-incidents/utils/paymentIncidents";
import { useWalletTopUpAccess } from "@/modules/dashboard/wallet-top-ups/hooks/useWalletTopUpAccess";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";

import { useOperationLabels } from "../../hooks/useOperationLabels";
import { useWalletMovements } from "../../hooks/useWalletMovements";
import type { PaymentMovement } from "../../types/payments-wallets.types";
import { MOVEMENT_OPEN_PARAM } from "../../utils/walletMovements";
import { WalletStatementDetailDialog } from "../WalletStatementDetailDialog";
import { MovementsFilters } from "./MovementsFilters";
import { MovementsTable } from "./MovementsTable";

export interface WalletMovementsTabProps {
  /** The tab was opened: nothing is read before that. */
  enabled: boolean;
  /** The page header's "Incluir Coloca" switch. */
  includeColoca: boolean;
  masked: boolean;
  currencies: string[];
  reloadSignal?: number;
}

/**
 * INTERNAL staff: every movement of every client wallet, to find the payment a client reports, and the
 * incidents opened on payouts that went wrong at the provider. Reading is open to staff; opening, following
 * and refunding an incident is for the wallet top-ups allowlist.
 */
export function WalletMovementsTab({
  enabled,
  includeColoca,
  masked,
  currencies,
  reloadSignal = 0,
}: WalletMovementsTabProps) {
  const { t } = useTranslations("wallets");
  const movements = useWalletMovements(enabled, includeColoca, reloadSignal);
  const [summaryReload, setSummaryReload] = useState(0);
  const incidents = usePaymentIncidentsSummary(enabled, summaryReload + reloadSignal);
  const { allowed: canManage } = useWalletTopUpAccess();
  const operationLabels = useOperationLabels();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get(MOVEMENT_OPEN_PARAM);
  // The open movement is kept apart from the page: an action refreshes the list, and the row may leave it
  // (filtered by the incident's old status, or pushed to the next page by the refund's own leg). Unmounting
  // the drawer then would take the 2FA challenge, the report or the refund receipt with it.
  const [pinned, setPinned] = useState<PaymentMovement | null>(null);
  const listed = openId ? (movements.rows.find((row) => row.id === openId) ?? null) : null;
  if (listed && listed !== pinned) setPinned(listed);
  const selected = listed ?? (pinned && pinned.id === openId ? pinned : null);

  const open = useCallback(
    (movement: PaymentMovement) => {
      setPinned(movement);
      setSearchParams((prev) => {
        prev.set(MOVEMENT_OPEN_PARAM, movement.id);
        return prev;
      });
    },
    [setSearchParams],
  );
  const close = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          prev.delete(MOVEMENT_OPEN_PARAM);
          return prev;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const { refresh, setFilters, filters } = movements;
  // A link to a movement that is not on the page it names would otherwise pop the drawer open later, the
  // first time some list happens to contain it.
  const stale = !!openId && !selected && movements.isLoaded && !movements.isLoading;
  useEffect(() => {
    if (stale) close();
  }, [close, stale]);
  const changed = useCallback(() => {
    refresh();
    setSummaryReload((value) => value + 1);
  }, [refresh]);

  if (!movements.hasAccess) return <NoAccessView />;

  return (
    <div className="space-y-6">
      <IncidentsSummaryStrip
        summary={incidents.summary}
        isLoading={incidents.isLoading}
        error={incidents.error}
        onRetry={incidents.retry}
        openSelected={filters.incident === "open"}
        onSelectOpen={() => setFilters({ incident: filters.incident === "open" ? null : "open" })}
        masked={masked}
      />

      <section className="space-y-4">
        <MovementsFilters
          filters={filters}
          onChange={setFilters}
          onReplace={movements.replaceFilters}
          onClear={movements.resetFilters}
          hasActiveFilters={movements.hasActiveFilters}
          currencies={currencies}
        />
        {movements.error ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={movements.error}
            actions={
              <ColocaButton variant="secondary" size="sm" onClick={refresh}>
                {t("retry")}
              </ColocaButton>
            }
          />
        ) : (
          <MovementsTable
            rows={movements.rows}
            total={movements.total}
            page={movements.page}
            onPageChange={movements.setPage}
            isLoading={movements.isLoading}
            masked={masked}
            hasActiveFilters={movements.hasActiveFilters}
            onClearFilters={movements.resetFilters}
            onOpen={open}
          />
        )}
      </section>

      <WalletStatementDetailDialog
        transaction={selected}
        wallet={{ id: selected?.wallet_id ?? "", currency: selected?.currency ?? "COP" }}
        operationLabels={operationLabels}
        organizationName={selected?.organization_name ?? null}
        size="lg"
        onClose={close}
        extra={
          selected && (selected.incident || canOpenIncidentOn(selected)) ? (
            <IncidentPanel
              key={selected.id}
              incidentId={selected.incident?.id ?? null}
              operation={{
                id: selected.operation_id,
                status: selected.operation_status,
                rail: selected.operation_rail,
                providerCode: selected.provider_code,
              }}
              canManage={canManage}
              onChanged={changed}
            />
          ) : null
        }
      />
    </div>
  );
}
